//@flow
import React from 'react';

import {makeStyles, IconButton, Typography, Divider} from '@material-ui/core';
import {Delete, Edit} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';

import type {ScheduleMessage} from '../../../../entities';
import {formatDateTime, timeAgo} from '../../../../utils/date';
import {classNames} from '../../../../utils/react';

const useStyles = makeStyles(theme => ({
  scheduleCard: {
    width: '100%',
    padding: '1rem',
    border: `1px solid ${colors.grey300}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
  },
  message: {
    fontSize: 14,
    color: colors.grey800,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  messageItemDate: {
    fontSize: 12,
    color: colors.grey600,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  messageLabelContainer: {
    display: 'flex',
    gap: '.25rem',
    flexDirection: 'column',
  },
  sent: {
    color: colors.success,
    fontWeight: 600,
  },
  error: {
    color: colors.error,
    fontWeight: 600,
  },
  statusLineBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 4,
    height: '100%',

    '&[data-status="PENDING"]': {
      backgroundColor: colors.grey500,
    },

    '&[data-status="SENT"]': {
      backgroundColor: colors.success,
    },

    '&[data-status="ERROR"]': {
      backgroundColor: colors.error,
    },
  },
}));

type Props = {
  message: ScheduleMessage,
  onDelete: (id: string) => void,
  onEdit: (id: string) => void,
};

export default function ScheduleMessageCard(props: Props) {
  const {message, onDelete, onEdit} = props;

  const classes = useStyles();
  const sent = message.crmScheduledMessageState === 'SENT' && !!message.sentAt;

  const timeLabel =
    sent && message.sentAt
      ? `Enviado hace ${timeAgo(message.sentAt)}`
      : formatDateTime(new Date(message.sendAt));

  const canEdit = message.crmScheduledMessageState === 'PENDING';
  const error = message.crmScheduledMessageState === 'ERROR';

  return (
    <div className={classes.scheduleCard}>
      <div
        className={classes.statusLineBar}
        data-status={message.crmScheduledMessageState}
      ></div>
      <Typography className={classes.message}>
        {message.message.text}
      </Typography>
      <Divider />
      <footer className={classes.footer}>
        <div className={classes.messageLabelContainer}>
          {error ? (
            <span className={classes.error}>Mensaje no enviado</span>
          ) : (
            <span
              className={classNames(classes.messageItemDate, {
                [classes.sent]: sent,
                [classes.error]: error,
              })}
            >
              {timeLabel}
            </span>
          )}
        </div>
        <div className={classes.actions}>
          <IconButton
            size="small"
            onClick={() => {
              onDelete(message.id);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => {
                onEdit(message.id);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </div>
      </footer>
    </div>
  );
}
